/**
 * "Antes de cada comando" (the owner, 22/10/2026; docs/refactor/50): the prompt box's tab that says what goes with
 * every request and what steers the agent — the project's instructions, this computer's, the skills switched on, and
 * the instruction files the agent reads by itself in the repository. It replaces the "Instruções fixas" tab, which
 * showed only the first of the four and sat behind "…".
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, FileText, Laptop, Pin, ShieldCheck, Wand2 } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { instructionFiles } from '../lib/instructionFiles'
import type { Permission } from '../lib/localAgent'

export interface BeforeEachCommandProps {
  desktop: boolean
  /** The project's pinned instructions. */
  pinned: Array<{ id: string; path: string; sizeBytes: number }>
  /** This computer's own instructions (desktop). */
  local: string
  /** What the agent may do here without asking (Conectar agente; docs/refactor/59); null on the web. */
  permission?: Permission | null
  /** The skills switched on here; null on the web. */
  skills: Array<{ name: string; description: string }> | null
  /** The open project's folder on this computer; null when none. */
  folder: string | null
  listFiles: (folder: string) => Promise<string[]>
  readFile: (folder: string, file: string) => Promise<string>
}

export function BeforeEachCommand({ desktop, pinned, local, permission = null, skills, folder, listFiles, readFile }: BeforeEachCommandProps) {
  const { t } = useI18n()
  const [files, setFiles] = useState<string[] | null>(null)
  const [opened, setOpened] = useState<{ file: string; text: string } | null>(null)

  useEffect(() => {
    let alive = true
    setFiles(null)
    if (folder) void listFiles(folder).then((all) => alive && setFiles(instructionFiles(all))).catch(() => alive && setFiles([]))
    return () => {
      alive = false
    }
  }, [folder, listFiles])

  const open = (file: string) => {
    if (!folder) return
    if (opened?.file === file) return setOpened(null)
    void readFile(folder, file).then((text) => setOpened({ file, text: text.slice(0, 6000) })).catch(() => setOpened({ file, text: t('along.unreadable') }))
  }

  const localFirst = local.trim().split('\n').find((line) => line.trim())?.trim() ?? ''

  return (
    <div className="stack before-each">
      <p className="tiny muted" style={{ margin: 0 }}>{t('along.intro')}</p>

      <Group icon={<Pin size={13} />} title={t('along.project')} count={pinned.length} edit={<Link to="/project/pinned">{t('along.change')}</Link>}>
        {pinned.length === 0 ? (
          <span className="tiny muted">{t('along.projectNone')}</span>
        ) : (
          <ul className="before-each__list">
            {pinned.map((doc) => (
              <li key={doc.id} className="tiny"><span className="mono">{doc.path}</span> <span className="muted">· {Math.max(1, Math.round(doc.sizeBytes / 1024))} KB</span></li>
            ))}
          </ul>
        )}
      </Group>

      <Group icon={<Laptop size={13} />} title={t('along.local')} count={desktop && localFirst ? 1 : 0} edit={<Link to="/project/pinned">{t('along.change')}</Link>}>
        <span className="tiny muted">
          {!desktop ? t('along.onThatComputer') : localFirst ? <>“{localFirst.length > 90 ? `${localFirst.slice(0, 89)}…` : localFirst}”</> : t('along.localNone')}
        </span>
      </Group>

      {/* The agent is told what it may do here (docs/refactor/59): the person sees the same line it reads. */}
      <Group icon={<ShieldCheck size={13} />} title={t('along.permission')} count={desktop && permission ? 1 : 0} edit={<Link to="/agents/connect">{t('along.change')}</Link>}>
        <span className="tiny muted">{!desktop || !permission ? t('along.onThatComputer') : t(`along.permission.${permission}`)}</span>
      </Group>

      <Group icon={<Wand2 size={13} />} title={t('along.skills')} count={skills?.length ?? 0} edit={<Link to="/settings/skills">{t('along.change')}</Link>}>
        {skills === null ? (
          <span className="tiny muted">{t('along.onThatComputer')}</span>
        ) : skills.length === 0 ? (
          <span className="tiny muted">{t('along.skillsNone')}</span>
        ) : (
          <ul className="before-each__list">
            {skills.map((skill) => (
              <li key={skill.name} className="tiny" title={skill.description}><span className="mono">{skill.name}</span>{skill.description && <span className="muted"> · {skill.description.length > 80 ? `${skill.description.slice(0, 79)}…` : skill.description}</span>}</li>
            ))}
          </ul>
        )}
      </Group>

      <Group icon={<BookOpen size={13} />} title={t('along.files')} count={files?.length ?? 0}>
        {!desktop || !folder ? (
          <span className="tiny muted">{t(desktop ? 'along.filesNoFolder' : 'along.onThatComputer')}</span>
        ) : files === null ? (
          <span className="tiny muted">{t('common.loading')}</span>
        ) : files.length === 0 ? (
          <span className="tiny muted">{t('along.filesNone')}</span>
        ) : (
          <>
            <span className="tiny muted">{t('along.filesHint')}</span>
            <ul className="before-each__list">
              {files.map((file) => (
                <li key={file}>
                  <button type="button" className="btn btn--ghost btn--sm before-each__file" onClick={() => open(file)} aria-expanded={opened?.file === file}>
                    <FileText size={12} aria-hidden /> <span className="mono">{file}</span>
                  </button>
                  {opened?.file === file && <pre className="tiny before-each__preview">{opened.text}</pre>}
                </li>
              ))}
            </ul>
          </>
        )}
      </Group>
    </div>
  )
}

function Group({ icon, title, count, edit, children }: { icon: JSX.Element; title: string; count: number; edit?: JSX.Element; children: JSX.Element }) {
  return (
    <section className="before-each__group">
      <header className="row row--tight small before-each__head">
        {icon} <strong>{title}</strong>
        {count > 0 && <span className="prompt-dock__tab-count">{count}</span>}
        <span className="spacer" />
        {edit && <span className="tiny">{edit}</span>}
      </header>
      {children}
    </section>
  )
}
